import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/* BMP-HOOK-START:variant-image-preload
 * Warms other colors' gallery images so the first swatch click doesn't wait on
 * a cold download (and a cold Shopify CDN resize of the 4096px original).
 * Sources: <template data-bmp-variant-preload> in
 * snippets/product-media-gallery-content.liquid, rendered with the gallery's
 * own srcset + sizes so the browser caches the exact candidate it will use. */
/** @type {Set<string>} */
const bmpPreloaded = new Set();

function bmpCanPreload() {
  // @ts-ignore - Network Information API is not in every lib.dom
  const connection = navigator.connection;
  if (connection?.saveData) return false;
  if (/(^|-)2g$/.test(connection?.effectiveType || '')) return false;
  return true;
}

/**
 * @param {HTMLImageElement} source
 * @param {'high' | 'low'} priority
 * @returns {Promise<void>}
 */
function bmpPreloadImage(source, priority) {
  const srcset = source.getAttribute('srcset');
  if (!srcset || bmpPreloaded.has(srcset)) return Promise.resolve();
  bmpPreloaded.add(srcset);

  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    // @ts-ignore - fetchPriority is widely supported but not in every lib.dom
    img.fetchPriority = priority;
    img.onload = img.onerror = () => resolve();
    img.sizes = source.getAttribute('sizes') || '100vw'; // sizes must be set before srcset
    img.srcset = srcset;
  });
}

/** @param {() => void} callback */
function bmpWhenIdle(callback) {
  const run = () =>
    'requestIdleCallback' in window ? window.requestIdleCallback(callback, { timeout: 3000 }) : setTimeout(callback, 1500);
  if (document.readyState === 'complete') run();
  else window.addEventListener('load', run, { once: true });
}
/* BMP-HOOK-END:variant-image-preload */

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // BMP-HOOK-START:variant-image-preload
    if (target && this.#preloadSources.length) {
      for (const type of ['pointerover', 'focusin']) {
        target.addEventListener(type, this.#handleSwatchIntent, { signal, passive: true });
      }
      target.addEventListener('touchstart', this.#handleSwatchIntent, { signal, passive: true });
      target.addEventListener('change', this.#handleSwatchChange, { signal });

      if (bmpCanPreload()) {
        bmpWhenIdle(async () => {
          // One at a time, low priority, so it never competes with the page itself.
          for (const img of this.#preloadSources) {
            if (signal.aborted) return;
            await bmpPreloadImage(img, 'low');
          }
        });
      }
    }
    // BMP-HOOK-END:variant-image-preload
  }

  // BMP-HOOK-START:variant-image-preload
  /** @returns {HTMLImageElement[]} */
  get #preloadSources() {
    const template = this.querySelector('template[data-bmp-variant-preload]');
    if (!(template instanceof HTMLTemplateElement)) return [];
    return Array.from(template.content.querySelectorAll('img[srcset]'));
  }

  /**
   * Swatch hover / touch / keyboard focus: fetch that color's image right away.
   * @param {Event} event
   */
  #handleSwatchIntent = (event) => {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest('label');
    const input = label?.control ?? event.target.closest('input');
    if (!(input instanceof HTMLInputElement) || !input.closest('variant-picker')) return;

    const variantId = input.dataset.variantId;
    if (!variantId) return;

    const match = this.#preloadSources.find((img) =>
      (img.dataset.variantIds || '').split(',').includes(variantId)
    );
    if (match) bmpPreloadImage(match, 'high');
  };

  /**
   * On a color click, show that color's (already preloaded) photo immediately
   * instead of waiting for the page re-render. The re-rendered gallery then
   * replaces this one with the same, already-cached image.
   * @param {Event} event
   */
  #handleSwatchChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.checked || !input.closest('variant-picker')) return;
    if (input.closest('product-card') || input.dataset.connectedProductUrl) return;

    const variantId = input.dataset.variantId;
    if (!variantId) return;

    const match = this.#preloadSources.find((img) =>
      (img.dataset.variantIds || '').split(',').includes(variantId)
    );
    if (!match) return;

    const slides = this.slideshow?.slides;
    const firstSlide = slides?.[0] ?? this.media?.[0];
    const image = firstSlide?.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return;

    Promise.resolve()
      .then(() => this.slideshow?.select(0, undefined, { animate: false }))
      .catch(() => {});

    image.removeAttribute('loading');
    image.sizes = match.getAttribute('sizes') || image.sizes;
    image.srcset = match.getAttribute('srcset') || '';
    image.src = match.getAttribute('src') || image.src;
  };
  // BMP-HOOK-END:variant-image-preload

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
